import { useEffect, useRef, useState } from 'react';

export default function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    'Áudio: Cura da Criança Interior',
    'Áudio: Limpeza de Relacionamentos Passados',
    'Áudio: Prosperidade e Abundância',
    'Ouça online — sem precisar baixar',
    'Acesso por 1 ano aos áudios',
    'Garantia de 7 dias',
  ];

  return (
    <section
      id="precos"
      ref={sectionRef}
      className="w-full py-16 md:py-24 bg-background-50"
      aria-label="Oferta especial"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider mb-3">
            Oferta Exclusiva
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Invista na sua transformação
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Um único pagamento. Acesso imediato. Garantia de 7 dias.
            Escolha a melhor opção para você — o pacote completo é a escolha mais inteligente.
          </p>
        </div>

        <div className="max-w-lg mx-auto mb-12">
          <div
            className={`relative bg-background-50 border-2 border-accent-500 rounded-2xl p-6 md:p-8 shadow-lg shadow-accent-500/10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-bold whitespace-nowrap shadow-md">
                <i className="ri-fire-line" />
                Melhor Oferta
              </span>
            </div>

            <div className="text-center mt-2 mb-6">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-1">
                Pacote Completo
              </h3>
              <p className="text-sm text-foreground-600">3 Áudios de Cura</p>
            </div>

            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-lg text-foreground-500">R$</span>
                <span className="font-heading text-5xl md:text-6xl font-extrabold text-foreground-900">
                  49,90
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-sm text-foreground-500 line-through">R$ 149,70</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                  Economize 67%
                </span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-double-line text-accent-500 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <div className="flex flex-col items-center gap-4">
              <div className="w-full text-center">
                <p className="text-sm font-medium text-foreground-700 mb-3">
                  Clique no botão abaixo e finalize sua compra com segurança na Hotmart
                </p>
                <a
                  href="https://pay.hotmart.com/T106583743Q?checkoutMode=2"
                  onClick={(e) => e.preventDefault()}
                  className="hotmart-fb hotmart__button-checkout inline-flex items-center justify-center w-full py-4 rounded-xl bg-accent-500 text-background-50 dark:text-foreground-950 font-bold text-base md:text-lg transition-all hover:bg-accent-600 hover:scale-[1.02] cursor-pointer whitespace-nowrap shadow-xl shadow-accent-500/25"
                >
                  <i className="ri-shopping-bag-3-line mr-2 text-xl" />
                  Quero Transformar Minha Vida Agora
                </a>
              </div>

              <div className="flex items-center justify-center gap-4 mt-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-lock-line text-accent-500" />
                  Pagamento Seguro
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-shield-check-line text-accent-500" />
                  Dados Protegidos
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`max-w-3xl mx-auto transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-center text-sm font-medium text-foreground-600 mb-5">
            Ou compre individualmente:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Áudio: Cura da Criança Interior', time: '28 min' },
              { title: 'Áudio: Limpeza de Relacionamentos Passados', time: '46 min' },
              { title: 'Áudio: Prosperidade e Abundância', time: '47 min' },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-background-100 border border-background-200/70 rounded-xl p-5 text-center transition-all hover:border-primary-300/50 relative"
              >
                <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">
                  {item.title}
                </h4>
                <p className="text-xs text-foreground-500 mb-3">{item.time}</p>
                <div className="flex items-baseline justify-center gap-0.5 mb-3">
                  <span className="text-sm text-foreground-500">R$</span>
                  <span className="font-heading text-2xl font-extrabold text-foreground-900">49,90</span>
                </div>
                <a
                  href="https://pay.hotmart.com/T106583743Q?checkoutMode=2"
                  onClick={(e) => e.preventDefault()}
                  className="hotmart-fb hotmart__button-checkout block w-full py-2 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-xs transition-all hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  Quero este
                </a>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-3 bg-accent-50 border border-accent-200/60 rounded-xl p-4 max-w-lg mx-auto">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-accent-500 mt-0.5">
              <i className="ri-sparkling-line text-background-50 text-sm" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900 mb-0.5">
                O super desconto de 67% é exclusivo do Pacote Completo!
              </p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Cada áudio custa R$ 49,90 se comprado separadamente — R$ 149,70 no total.
                No pacote você leva os 3 pelo preço de 1: apenas <strong className="text-accent-700">R$ 49,90</strong>.
              </p>
            </div>
          </div>
        </div>

        <div
          className={`max-w-2xl mx-auto mt-10 text-center transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="flex items-center gap-2 text-sm text-foreground-600 whitespace-nowrap">
              <i className="ri-refresh-line text-accent-500 text-lg" />
              <strong>7 dias de garantia</strong> ou seu dinheiro de volta
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600 whitespace-nowrap">
              <i className="ri-flashlight-line text-accent-500 text-lg" />
              Receba <strong>imediatamente</strong> após confirmação
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}